// 2026-05-26 - FormuLearning - Step 1 제조사 목록 섹션
// 파일 위치: frontend/src/features/step1/ManufacturerSection.tsx
// 한 제조사가 여러 DMF 등록(염 형태·변경등록 등)을 갖는 경우가 많아 회사명으로 묶어 표시한다.

import { Fragment, useState } from 'react'
import Collecting from './Collecting'

interface Props {
  manufacturers: Record<string, string | null>[]
  loading?: boolean
}

interface Group {
  company: string
  country: string | null
  rows: Record<string, string | null>[]
}

function groupByCompany(list: Record<string, string | null>[]): Group[] {
  const map = new Map<string, Group>()
  for (const m of list) {
    const company = m.company || '—'
    let g = map.get(company)
    if (!g) {
      g = { company, country: m.country ?? null, rows: [] }
      map.set(company, g)
    }
    if (!g.country && m.country) g.country = m.country
    g.rows.push(m)
  }
  return Array.from(map.values())
}

export default function ManufacturerSection({ manufacturers, loading }: Props) {
  const [openSet, setOpenSet] = useState<Set<number>>(new Set())
  const groups = groupByCompany(manufacturers)
  const allOpen = groups.length > 0 && openSet.size === groups.length

  const toggle = (i: number) =>
    setOpenSet((prev) => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })

  const toggleAll = () => setOpenSet(allOpen ? new Set() : new Set(groups.map((_, i) => i)))

  return (
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">API Manufacturers</h4>
        {manufacturers.length > 0 && (
          <>
            <span className="text-[10px] text-text-muted">
              {groups.length}개사 · DMF 등록 {manufacturers.length}건
            </span>
            <button
              onClick={toggleAll}
              className="rounded px-1.5 py-0.5 text-[10px] font-medium transition-opacity hover:opacity-80"
              style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}
            >
              {allOpen ? 'Collapse all' : 'Show all'}
            </button>
          </>
        )}
      </div>

      {manufacturers.length === 0 ? (
        loading ? <Collecting /> : <p className="text-[11px] text-text-muted">표시할 제조사 정보가 없습니다.</p>
      ) : (
        <div className="panel-in overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">Country</th>
                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">Company</th>
                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">DMF 등록</th>
                <th className="px-3 py-1.5 text-right font-medium text-text-secondary"></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g, i) => {
                const open = openSet.has(i)
                const multi = g.rows.length > 1
                return (
                  <Fragment key={i}>
                    <tr
                      onClick={() => toggle(i)}
                      className="cursor-pointer border-t"
                      style={{ borderColor: 'var(--border-subtle)', backgroundColor: open ? 'var(--bg-row-active)' : 'transparent' }}
                    >
                      <td className="px-3 py-1.5 text-text-body">{g.country || '—'}</td>
                      <td className="px-3 py-1.5 text-text-body">{g.company}</td>
                      <td className="px-3 py-1.5 text-text-muted">{g.rows.length}건</td>
                      <td className="px-3 py-1.5 text-right text-[9px] text-text-secondary">{open ? '▲' : '▼'}</td>
                    </tr>
                    {open && (
                      <tr style={{ backgroundColor: 'var(--bg-row-active)' }}>
                        <td colSpan={4} className="px-3 py-2">
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr className="text-text-secondary">
                                <th className="py-0.5 text-left font-medium">KDMF</th>
                                <th className="py-0.5 text-left font-medium">등록일</th>
                                <th className="py-0.5 text-left font-medium">제조소</th>
                                <th className="py-0.5 text-left font-medium">등록업체</th>
                              </tr>
                            </thead>
                            <tbody>
                              {g.rows.map((r, j) => (
                                <tr key={j} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                  <td className="py-0.5 pr-2 text-text-body">{r.kdmf || '—'}</td>
                                  <td className="py-0.5 pr-2 text-text-muted">{r.dmf_permit_date || '—'}</td>
                                  <td className="py-0.5 pr-2 text-text-muted">{r.manuf_place || '—'}</td>
                                  <td className="py-0.5 text-text-muted">{r.registrant || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {multi && (
                            <p className="mt-1 text-[9px] text-text-muted">
                              ※ 동일 제조사의 복수 등록 — 염 형태(예: 시트르산염) 또는 변경등록(번호 끝 (1) 등)으로 분리됩니다.
                            </p>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
